/**
 * TechCheckPv — Iter88u (2026-02-19)
 *
 * PV-spezifische Vor-Ort-Aufnahme:
 *  1. Dach & Ausrichtung
 *  2. DC-Weg & Wechselrichter
 *  3. Batterie & Backup
 *  4. Verschattung & Umgebung
 *  5. Zählerschrank & Einspeisung
 *  6. Grundriss / Fassade
 *
 * Speichert unter demselben `technical_check`-Feld wie WP (namespaced mit
 * `pv_`-Prefix in den Feldnamen, damit keine Kollision mit WP-Feldern).
 * Bilder-Kategorien sind mit „PV — <Sektion>" prefixed.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, ChevronDown, ChevronUp, CheckCircle2, Camera, ImagePlus, ListTodo, AlertCircle, Sparkles } from "lucide-react";
import { runOrQueue } from "@/lib/offlineQueue";
import { ThemePhotoButton } from "@/components/ThemePhotoButton";
import { SecureMedia } from "@/components/SecureMedia";
import { DictaphoneTextarea } from "@/components/DictaphoneTextarea";

const inputCls = "h-12 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-4 text-[15px] text-zinc-50 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] transition outline-none";
const labelCls = "block text-[13px] font-semibold text-zinc-100 mb-2";

const PV_SECTIONS = [
  {
    id: "pv_dach",
    label: "1 · Dach & Ausrichtung",
    category: "PV — Dach",
    description: "Dachart, Neigung, Himmelsrichtung, Deckung, Alter, Zustand — Grundlage für Modulplanung.",
    fields: [
      ["pv_roof_type", "Dachart", "select", ["Satteldach","Walmdach","Zeltdach","Pultdach","Mansarddach","Flachdach","Sonstiges"]],
      ["pv_roof_covering", "Dacheindeckung", "select", ["Ziegel (Ton)","Betonstein","Trapezblech","Bitumenschindel","Schiefer","Bitumenbahn","Sonstiges"]],
      ["pv_roof_azimuth", "Ausrichtung Hauptdachfläche (° von Süd)", "number"],
      ["pv_roof_tilt_deg", "Dachneigung (°)", "number"],
      ["pv_roof_area_m2", "Nutzbare Dachfläche (m²)", "number"],
      ["pv_roof_age_years", "Alter Dacheindeckung (Jahre)", "number"],
      ["pv_roof_condition", "Zustand Dacheindeckung", "select", ["neuwertig","gut","mittel","sanierungsbedürftig"]],
      ["pv_roof_asbestos", "Asbestverdacht?", "bool"],
      ["pv_snow_load_zone", "Schneelastzone", "select", ["Zone 1","Zone 2","Zone 3","unbekannt"]],
      ["pv_wind_load_zone", "Windlastzone", "select", ["Zone 1","Zone 2","Zone 3","Zone 4","unbekannt"]],
      ["pv_dormers_present", "Gauben/Dachfenster vorhanden?", "bool"],
      ["pv_roof_notes", "Notizen zum Dach", "textarea"],
    ],
    photoSpots: [
      { key: "PV — Dach — Gesamtansicht", label: "Dach-Gesamtansicht (mit Umgebung)" },
      { key: "PV — Dach — Nahaufnahme Ziegel", label: "Nahaufnahme Dachziegel" },
      { key: "PV — Dach — Dachfenster/Gauben", label: "Dachfenster / Gauben (falls vorhanden)" },
      { key: "PV — Dach — Kamin/Antenne", label: "Kamin / Antenne (Verschattungsobjekte)" },
    ],
  },
  {
    id: "pv_wechselrichter",
    label: "2 · Wechselrichter & DC-Weg",
    category: "PV — Wechselrichter",
    description: "Aufstellort Wechselrichter, DC-Kabelweg vom Dach zum WR, Länge und Zugänglichkeit.",
    fields: [
      ["pv_wr_location", "Aufstellort Wechselrichter", "select", ["Keller","Hauswirtschaftsraum","Dachboden","Garage","Außenwand","Sonstiges"]],
      ["pv_wr_distance_to_dach_m", "Kabelweg Dach → WR (m)", "number"],
      ["pv_wr_ventilation_ok", "Ausreichende Belüftung / max. 40°C?", "bool"],
      ["pv_wr_wall_type", "Wandbeschaffenheit für Montage", "select", ["Massiv (Beton/Ziegel)","Trockenbau","Holz","Sonstiges"]],
      ["pv_dc_route_inside", "DC-Weg innen möglich?", "bool"],
      ["pv_dc_route_outside", "DC-Weg außen möglich?", "bool"],
      ["pv_dc_core_drilling_required", "Kernbohrung erforderlich?", "bool"],
      ["pv_dc_number_strings_expected", "Erwartete Anzahl Strings", "number"],
      ["pv_ac_distance_to_zaehler_m", "AC-Kabelweg WR → Zählerschrank (m)", "number"],
      ["pv_wr_notes", "Notizen zum WR / DC-Weg", "textarea"],
    ],
    photoSpots: [
      { key: "PV — WR — Aufstellort", label: "Geplanter WR-Standort" },
      { key: "PV — WR — DC-Weg vom Dach", label: "DC-Kabelweg vom Dach" },
      { key: "PV — WR — Wandzustand", label: "Wandzustand am Montageort" },
    ],
  },
  {
    id: "pv_batterie",
    label: "3 · Batterie & Notstrom",
    category: "PV — Batterie",
    description: "Batterie-Standort, gewünschte Kapazität, Notstromfunktion (Backup-Box), Netzumschaltung.",
    fields: [
      ["pv_battery_desired", "Batteriespeicher gewünscht?", "bool"],
      ["pv_battery_capacity_kwh", "Gewünschte Kapazität (kWh)", "number"],
      ["pv_battery_location", "Aufstellort Batterie", "select", ["Keller","Hauswirtschaftsraum","Garage","Neben Wechselrichter","Sonstiges"]],
      ["pv_battery_floor_load_ok", "Bodenbelastbarkeit ausreichend?", "bool"],
      ["pv_battery_backup_desired", "Notstromfunktion (Backup-Box) gewünscht?", "bool"],
      ["pv_battery_backup_type", "Backup-Modus", "select", ["Ersatzstrom (nur ausgewählte Kreise)","Notstrom (Insel)","keine Präferenz"]],
      ["pv_battery_ev_charger_integration", "E-Auto-Wallbox in Energiemanagement einbinden?", "bool"],
      ["pv_battery_notes", "Notizen zur Batterie / Backup", "textarea"],
    ],
    photoSpots: [
      { key: "PV — Batterie — Standort", label: "Geplanter Batterie-Standort" },
      { key: "PV — Batterie — Umgebung", label: "Umgebung (Belüftung, Temperatur)" },
    ],
  },
  {
    id: "pv_verschattung",
    label: "4 · Verschattung & Umgebung",
    category: "PV — Verschattung",
    description: "Bäume, Nachbargebäude, Schornstein, Antenne, Sat-Schüssel — alles was Schatten wirft.",
    fields: [
      ["pv_shading_trees", "Bäume in <15m Abstand?", "bool"],
      ["pv_shading_trees_side", "Bäume auf welcher Seite?", "select", ["Süd","Ost","West","Nord","mehrere Seiten"]],
      ["pv_shading_neighbor_building", "Nachbargebäude verschattet?", "bool"],
      ["pv_shading_chimney", "Schornstein auf Dach?", "bool"],
      ["pv_shading_antenna_sat", "Antenne / Sat-Schüssel?", "bool"],
      ["pv_shading_dormers", "Gauben verschatten Fläche?", "bool"],
      ["pv_shading_estimated_percent", "Geschätzte Verschattung (%)", "number"],
      ["pv_shading_notes", "Notizen zur Verschattung", "textarea"],
    ],
    photoSpots: [
      { key: "PV — Verschattung — Süd", label: "Blick nach Süden (aufsteigend)" },
      { key: "PV — Verschattung — Ost", label: "Blick nach Osten" },
      { key: "PV — Verschattung — West", label: "Blick nach Westen" },
      { key: "PV — Verschattung — Objekte", label: "Schornstein / Antennen / Sat" },
    ],
  },
  {
    id: "pv_zaehlerschrank",
    label: "5 · Zählerschrank & Einspeisung",
    category: "PV — Zählerschrank",
    description: "Zählerschrank-Zustand, PV-Zählerplatz, Rundsteuerempfänger, Einspeisebegrenzung.",
    fields: [
      ["pv_cabinet_type", "Zählerschrank-Typ", "select", ["modern (TAB-konform)","alt (vor 2016)","leer / neuwertig"]],
      ["pv_cabinet_free_slots", "Freie Zählerplätze", "number"],
      ["pv_cabinet_needs_replacement", "Zählerschrank-Tausch nötig?", "bool"],
      ["pv_meter_type", "Zählertyp", "select", ["Ferraris (alt)","moderner Zähler","Smart Meter","unbekannt"]],
      ["pv_bidirectional_meter_available", "Zweirichtungszähler vorhanden?", "bool"],
      ["pv_pv_meter_needed", "Extra PV-Erzeugungszähler nötig?", "bool"],
      ["pv_rse_required", "Rundsteuerempfänger (RSE) erforderlich?", "bool"],
      ["pv_einspeisebegrenzung", "Einspeisebegrenzung", "select", ["keine","70% (alt)","dynamisch (§14a)","unbekannt"]],
      ["pv_ueberspannungsschutz", "Überspannungsschutz vorhanden?", "bool"],
      ["pv_cabinet_notes", "Notizen zum Zählerschrank", "textarea"],
    ],
    photoSpots: [
      { key: "PV — Zählerschrank — Geschlossen", label: "Zählerschrank geschlossen" },
      { key: "PV — Zählerschrank — Offen", label: "Zählerschrank offen" },
      { key: "PV — Zählerschrank — Etikett", label: "Etikett Innentür" },
      { key: "PV — Zählerschrank — Freie Plätze", label: "Freie Reihen für PV-Komponenten" },
    ],
  },
  {
    id: "pv_grundriss",
    label: "6 · Grundriss / Fassade",
    category: "PV — Grundriss",
    description: "Grundrissfotos + Hausansichten für die spätere Modul-Layout-Planung durch Auftragnehmer.",
    fields: [
      ["pv_house_length_m", "Hauslänge (m)", "number"],
      ["pv_house_width_m", "Hausbreite (m)", "number"],
      ["pv_stockwerke", "Anzahl Stockwerke", "number"],
      ["pv_traufhoehe_m", "Traufhöhe (m)", "number"],
      ["pv_firsthoehe_m", "Firsthöhe (m)", "number"],
      ["pv_gaubenanzahl", "Anzahl Gauben", "number"],
      ["pv_grundriss_notes", "Notizen", "textarea"],
    ],
    photoSpots: [
      { key: "PV — Grundriss — Erdgeschoss", label: "Erdgeschoss-Grundriss" },
      { key: "PV — Grundriss — Obergeschoss", label: "Obergeschoss-Grundriss" },
      { key: "PV — Grundriss — Dachgeschoss", label: "Dachgeschoss-Grundriss" },
      { key: "PV — Fassade — Nord", label: "Fassade Nord" },
      { key: "PV — Fassade — Süd", label: "Fassade Süd" },
      { key: "PV — Fassade — Ost", label: "Fassade Ost" },
      { key: "PV — Fassade — West", label: "Fassade West" },
    ],
  },
];

// ---------- Field renderer (identisch zu WP-Version) ----------
const FieldInput = ({ f, value, onChange }) => {
  const [key, label, type, options] = f;
  if (type === "bool") {
    return (
      <div className="bg-[#09090B] border border-zinc-700 rounded-xl p-3 md:px-4 md:py-2.5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <span className="text-[14px] text-zinc-100 font-medium leading-snug pr-1">{label}</span>
          <div className="flex gap-1 self-stretch sm:self-auto">
            {[{ v: true, label: "Ja" }, { v: false, label: "Nein" }, { v: null, label: "—" }].map((o) => (
              <button
                key={String(o.v)}
                data-testid={`tech-pv-${key}-${o.v}`}
                onClick={() => onChange(o.v)}
                className={`flex-1 sm:flex-none h-9 px-3 min-w-[58px] rounded-lg text-[13px] font-semibold transition active:scale-95 ${
                  value === o.v ? "bg-[#00E575] text-[#09090B]" : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"
                }`}
              >{o.label}</button>
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (type === "select") {
    return (
      <div>
        <label className={labelCls}>{label}</label>
        <select data-testid={`tech-pv-${key}`} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">– bitte wählen –</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (type === "textarea") {
    return (
      <div className="md:col-span-2">
        <label className={labelCls}>{label}</label>
        <DictaphoneTextarea value={value || ""} onChange={onChange} rows={3} testid={`tech-pv-${key}`} placeholder="Notiz eingeben oder Diktieren…" />
      </div>
    );
  }
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        data-testid={`tech-pv-${key}`}
        type={type}
        className={inputCls}
        value={value ?? ""}
        onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
      />
    </div>
  );
};

function Section({ sec, isOpen, onToggle, form, onFieldChange, photos, projectId, onUpdate }) {
  const filled = sec.fields.filter((f) => {
    const v = form[f[0]];
    return v !== null && v !== "" && v !== undefined;
  }).length;
  const total = sec.fields.length;
  const complete = filled === total;
  const hasAnyField = filled > 0;
  const sectionPhotos = (photos || []).filter((p) => (p.category || "") === sec.category || (p.category || "").startsWith(`${sec.category} — `));
  const individualPhotos = sectionPhotos.filter((p) => (p.category || "") === sec.category);
  const hasIndividualPhotos = individualPhotos.length > 0;
  const [showFields, setShowFields] = useState(false);
  const [showIndividual, setShowIndividual] = useState(false);
  const [showSpots, setShowSpots] = useState(false);
  const spotsTaken = (sec.photoSpots || []).filter((spot) => (photos || []).some((p) => p.category === spot.key)).length;
  const spotsTotal = (sec.photoSpots || []).length;
  const spotsComplete = spotsTotal > 0 && spotsTaken === spotsTotal;
  const cardRef = useRef(null);
  const prevOpenRef = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !prevOpenRef.current && cardRef.current) {
      const t = setTimeout(() => cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      prevOpenRef.current = isOpen;
      return () => clearTimeout(t);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen]);

  return (
    <div ref={cardRef} className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden" style={{ scrollMarginTop: "140px" }}>
      <div className="p-3 md:p-4 hover:bg-zinc-800/30 transition">
        <button data-testid={`tech-pv-section-toggle-${sec.id}`} onClick={onToggle} className="w-full flex items-start gap-3 text-left">
          <div className={`h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 font-bold ${complete ? "bg-[#00E575] text-[#09090B]" : "bg-zinc-800 text-zinc-100"}`}>
            {complete ? <CheckCircle2 size={20} /> : <span className="text-[12px] font-mono">{filled}/{total}</span>}
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <div className="font-display font-bold text-[15px] sm:text-[16px] text-white leading-snug">{sec.label}</div>
            <div className="text-[13px] text-zinc-200 font-semibold mt-1 leading-snug">{sec.description}</div>
            <div className="text-[12px] text-zinc-300 mt-1 leading-snug">
              {filled} von {total} Feldern · {sectionPhotos.length} Foto{sectionPhotos.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="h-10 w-10 rounded-xl bg-zinc-800/60 flex items-center justify-center flex-shrink-0 self-start">
            {isOpen ? <ChevronUp size={20} className="text-zinc-300" /> : <ChevronDown size={20} className="text-zinc-300" />}
          </div>
        </button>
      </div>
      {isOpen && (
        <div className="px-4 pb-5 pt-1 space-y-4">
          {/* Pflichtfotos */}
          {sec.photoSpots?.length > 0 && (
            <div>
              <button type="button" data-testid={`tech-pv-section-toggle-spots-${sec.id}`} onClick={() => setShowSpots((s) => !s)}
                className={`w-full h-12 rounded-xl border-2 flex items-center justify-between gap-3 px-4 transition active:scale-[0.99] ${
                  !spotsComplete && !showSpots ? "bg-red-500/10 border-red-500/70 text-red-100" : showSpots ? "bg-[#00E575]/10 border-[#00E575]/40 text-zinc-100" : "bg-zinc-900/60 border-zinc-700 text-zinc-200 hover:border-zinc-500"
                }`}>
                <span className="inline-flex items-center gap-2 min-w-0">
                  <Camera size={16} className={!spotsComplete && !showSpots ? "text-red-400" : "text-[#00E575]"} />
                  <span className="text-[13.5px] font-semibold truncate">
                    {showSpots ? "Pflichtfotos ausblenden" : "Pflichtfotos"}
                    <span className="text-[12px] font-bold text-red-300 ml-1.5 uppercase tracking-wide">(Pflicht)</span>
                  </span>
                  {!spotsComplete && !showSpots && (
                    <span className="inline-flex items-center gap-1 text-[12px] uppercase tracking-wider font-bold text-red-200 bg-red-500/20 border border-red-500/50 rounded-full px-2 py-0.5">
                      <AlertCircle size={10} /> {spotsTaken}/{spotsTotal}
                    </span>
                  )}
                  {spotsComplete && !showSpots && (
                    <span className="inline-flex items-center gap-1 text-[12px] uppercase tracking-wider font-bold text-[#00E575] bg-[#00E575]/15 border border-[#00E575]/30 rounded-full px-2 py-0.5">
                      <CheckCircle2 size={10} /> {spotsTaken}/{spotsTotal}
                    </span>
                  )}
                </span>
                {showSpots ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showSpots && (
                <div className="mt-3 bg-[#09090B] border border-zinc-800 rounded-xl p-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {sec.photoSpots.map((spot) => {
                      const spotCount = (photos || []).filter((p) => p.category === spot.key).length;
                      return (
                        <ThemePhotoButton key={spot.key} projectId={projectId} category={spot.key} description={spot.label} label={spot.label}
                          variant="card" testid={`tech-pv-spot-${sec.id}-${spot.key.replace(/[^a-z0-9]/gi, "_")}`}
                          existingCount={spotCount} onUploaded={onUpdate} />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Individualfotos */}
          <div>
            <button type="button" onClick={() => setShowIndividual((s) => !s)}
              data-testid={`tech-pv-section-toggle-individual-${sec.id}`}
              className={`w-full h-12 rounded-xl border flex items-center justify-between gap-3 px-4 transition active:scale-[0.99] ${
                hasIndividualPhotos && !showIndividual ? "bg-amber-500/10 border-amber-500/50 text-amber-100" : showIndividual ? "bg-[#00E575]/10 border-[#00E575]/40 text-zinc-100" : "bg-zinc-900/60 border-zinc-700 text-zinc-200 hover:border-zinc-500"
              }`}>
              <span className="inline-flex items-center gap-2 min-w-0">
                <ImagePlus size={16} className={hasIndividualPhotos ? "text-amber-300" : "text-[#00E575]"} />
                <span className="text-[13.5px] font-semibold truncate">{showIndividual ? "Individualfotos ausblenden" : "Individualfotos"}</span>
                {hasIndividualPhotos && !showIndividual && (
                  <span className="inline-flex items-center gap-1 text-[12px] uppercase tracking-wider font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded-full px-2 py-0.5">
                    <AlertCircle size={10} /> {individualPhotos.length} vorhanden
                  </span>
                )}
              </span>
              {showIndividual ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {showIndividual && (
              <div className="mt-3 space-y-3">
                <div className="flex justify-end">
                  <ThemePhotoButton projectId={projectId} category={sec.category} description={`${sec.label} – Individualfoto`} label="+ Foto aufnehmen"
                    size="sm" testid={`tech-pv-section-more-photo-${sec.id}`} onUploaded={onUpdate} />
                </div>
                {individualPhotos.length === 0 ? (
                  <div className="bg-[#09090B] border border-dashed border-zinc-700 rounded-xl p-5 text-center text-[13px] text-zinc-500">
                    Noch keine Individualfotos für „{sec.category}".
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                    {individualPhotos.map((p) => (
                      <div key={p.id} className="bg-[#09090B] border border-zinc-800 rounded-xl overflow-hidden">
                        <div className="aspect-square bg-zinc-950">
                          <SecureMedia path={p.storage_path} contentType={p.content_type} alt={p.description} className="w-full h-full object-cover" />
                        </div>
                        {p.description && <div className="p-2"><div className="text-[12px] text-zinc-300 truncate mt-0.5">{p.description}</div></div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Detailfragen */}
          <div>
            <button type="button" onClick={() => setShowFields((s) => !s)}
              data-testid={`tech-pv-section-toggle-fields-${sec.id}`}
              className={`w-full h-12 rounded-xl border flex items-center justify-between gap-3 px-4 transition active:scale-[0.99] ${
                hasAnyField && !showFields ? "bg-amber-500/10 border-amber-500/50 text-amber-100" : showFields ? "bg-[#00E575]/10 border-[#00E575]/40 text-zinc-100" : "bg-zinc-900/60 border-zinc-700 text-zinc-200 hover:border-zinc-500"
              }`}>
              <span className="inline-flex items-center gap-2 min-w-0">
                <ListTodo size={16} className={hasAnyField && !showFields ? "text-amber-300" : "text-[#00E575]"} />
                <span className="text-[13.5px] font-semibold truncate">{showFields ? "Detail-Fragen ausblenden" : "Detail-Fragen"}</span>
                {hasAnyField && !showFields && (
                  <span className="inline-flex items-center gap-1 text-[12px] uppercase tracking-wider font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded-full px-2 py-0.5">
                    <AlertCircle size={10} /> {filled} Eingaben
                  </span>
                )}
              </span>
              <span className="text-[11.5px] text-zinc-400 font-mono inline-flex items-center gap-1">
                {filled}/{total} {showFields ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </span>
            </button>
            {showFields && (
              <div data-testid={`tech-pv-section-fields-${sec.id}`} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {sec.fields.map((f) => <FieldInput key={f[0]} f={f} value={form[f[0]]} onChange={(v) => onFieldChange(f[0], v)} />)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TechCheckPv({ project, tech, photos = [], onUpdate }) {
  const [form, setForm] = useState(tech || {});
  const [open, setOpen] = useState("pv_dach");
  const [loading, setLoading] = useState(false);
  const onFieldChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setLoading(true);
    try {
      // WICHTIG: nur die pv_-Felder speichern, damit die WP-Felder nicht überschrieben werden.
      const pvOnly = Object.fromEntries(Object.entries(form).filter(([k]) => k.startsWith("pv_")));
      // Merge: bestehende tech-Daten (inkl. WP) + neue pv_-Werte
      const merged = { ...(tech || {}), ...pvOnly };
      const r = await runOrQueue({
        method: "post",
        url: `/projects/${project.id}/technical-check`,
        data: merged,
        label: "PV-Technik-Check",
      });
      if (r.offline) toast.success("Offline gespeichert – wird später synchronisiert");
      else { toast.success("PV-Technik-Daten gespeichert"); onUpdate?.(); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-3" data-testid="tech-pv-root">
      <div className="rounded-xl bg-sky-500/8 border border-sky-500/25 p-3">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={16} className="text-sky-300" />
          <h3 className="text-[14px] font-bold text-zinc-50">PV-Vor-Ort-Aufnahme</h3>
        </div>
        <p className="text-[12px] text-zinc-300 leading-snug">
          Alle Angaben und Fotos aus diesen 6 Bereichen fließen später automatisch in die
          <b> Installations-Beschreibung für den Auftragnehmer</b> ein (Button „Installations-Brief" oben rechts im Technik-Tab).
        </p>
      </div>
      {PV_SECTIONS.map((sec) => (
        <Section key={sec.id} sec={sec} isOpen={open === sec.id} onToggle={() => setOpen(open === sec.id ? null : sec.id)}
          form={form} onFieldChange={onFieldChange} photos={photos} projectId={project.id} onUpdate={onUpdate} />
      ))}
      <button data-testid="tech-pv-save-btn" onClick={save} disabled={loading}
        className="h-14 w-full bg-[#00E575] text-[#09090B] font-bold text-[17px] rounded-xl hover:bg-[#00C964] glow-green active:scale-[0.97] transition flex items-center justify-center">
        {loading ? <Loader2 className="animate-spin" /> : <><Save size={20} className="mr-2" /> PV-Technik speichern</>}
      </button>
    </div>
  );
}
